import {isTypeActiveSubstance, isTypeSolution, isTypeSupportiveMedication} from '@/modules/drug-catalog/functions'
import {DataList, DataListItem} from '@cypro/ui/components/ui/data-list'
import {DataSection} from '@cypro/ui/components/ui/data-section'
import {Status} from '@cypro/ui/components/ui/status'
import {DrugTag} from '@/shared/components/ui/tags/drug-tag'
import {UserTag} from '@/shared/components/ui/tags/user-tag'
import {getDatetimeFormatter} from '@cypro/ui/utils'
import {Alert, Box, HStack, Link, Span, Tabs, Text} from '@chakra-ui/react'
import {TradeName} from '@cypro/engine/schema'
import {useTranslate} from '@tolgee/react'
import {useMemo} from 'react'
import {LuExternalLink} from 'react-icons/lu'

interface Props {
  tradeName: TradeName
}

export function AboutTabContent({tradeName}: Props) {
  const {t} = useTranslate('common')

  const {about, additionals, properties, reconstitution, logistics, consumables} = useMemo(() => {
    const type = tradeName?.medicationProfile?.drugCatalogItem?.catalogType

    const properties: DataListItem[] = [
      {
        label: t('medicine_form'),
        value: tradeName?.medicationProfile?.name || tradeName?.medicationProfile?.medicineForm?.name,
      },
      {label: t('container_type'), value: tradeName?.containerType?.name},
      {label: t('container_material'), value: tradeName?.containerMaterial?.name},
    ]

    const additionals: DataListItem[] = []
    const reconstitution: DataListItem[] = []

    const consumables: DataListItem[] =
      tradeName?.consumables?.map(({consumable, units}) => {
        if (consumable)
          return {
            label: `x ${units}`,
            value: consumable?.name,
          }

        return {label: '-', value: t('unknown')}
      }) || []

    if (isTypeActiveSubstance(type)) {
      const hasReconstitutionSolvents = Boolean(tradeName.reconstitutionSolvents?.length)

      reconstitution.push(
        ...(hasReconstitutionSolvents
          ? [
              {
                label: t('solvents'),
                value:
                  tradeName.reconstitutionSolvents
                    ?.map(solvent =>
                      solvent.solventVolume != null
                        ? `${solvent.solutionDrug?.name} - ${solvent.solventVolume} ml`
                        : solvent.solutionDrug?.name
                    )
                    .join(', ') || '',
              },
            ]
          : []),
        {label: t('concentration'), value: tradeName.reconstitutedSolutionConcentration},
        {label: t('concentration_unit'), value: tradeName.reconstitutedConcentrationUnit?.ucumCode},
        {label: t('volume_expansion'), value: tradeName.reconstitutedSolutionVolumeExpansion}
      )
    }

    if (isTypeActiveSubstance(type) || isTypeSupportiveMedication(type)) {
      properties.push(
        {label: t('strength_value'), value: tradeName.strengthValue},
        {label: t('strength_unit'), value: tradeName.strengthUnit?.ucumCode}
      )
    }

    if (tradeName?.medicationProfile?.isDrySubstance === false) {
      properties.push({label: t('volume_value'), value: tradeName.volumeValue})
    }

    if (isTypeSolution(type)) {
      properties.push(
        {label: t('container_contents'), value: tradeName.containerContents ? t(tradeName.containerContents) : null},
        {label: t('max_volume_capacity'), value: tradeName.maxVolumeCapacity},
        {label: t('concentration_value'), value: tradeName.concentrationValue},
        {label: t('concentration_unit'), value: tradeName.concentrationUnit?.ucumCode}
      )
    }

    if (isTypeSolution(type) || isTypeSupportiveMedication(type)) {
      additionals.push(
        {
          label: t('additionals_used'),
          value: tradeName.additionalsUsed ? t('yes') : t('no'),
        },
        {label: t('additionals_details'), value: tradeName.additionalsDetails},
        {label: t('additionals_weight'), value: tradeName.additionalsWeight}
      )
    }

    properties.push({label: t('density'), value: tradeName.density})

    properties.push({
      label: t('settings.minimumSaveableVolume'),
      value: tradeName?.minimumSaveableVolume ? `${tradeName.minimumSaveableVolume}%` : null,
    })

    return {
      about: [
        {label: t('trade_name_id'), value: tradeName.id},
        {label: t('name'), value: tradeName.name},

        {label: t('type'), value: type ? t(type) : null},
        {
          label:
            isTypeActiveSubstance(type) || isTypeSupportiveMedication(type)
              ? t('active_substance')
              : t('carrier_solution'),
          value: tradeName.medicationProfile?.drugCatalogItem?.name ? (
            <DrugTag drug={tradeName.medicationProfile.drugCatalogItem} />
          ) : null,
          t,
        },
        {label: t('prescribed_name'), value: tradeName.prescribedName?.name},
        {label: t('manufacturer'), value: tradeName.manufacturer?.name},
        {label: t('units_per_package'), value: tradeName.unitsPerPackage},
        {label: t('minimum_stock'), value: tradeName.minimumStock},
        {
          label: t('req_additional_approval'),
          value: tradeName.additionalApprovalRequired && isTypeActiveSubstance(type) ? t('yes') : t('no'),
        },
        {
          label: t('contains_alcohol'),
          value: tradeName.containsAlcohol && isTypeActiveSubstance(type) ? t('yes') : t('no'),
        },
        {
          label: t('trial_drug'),
          value: tradeName.isTrial ? t('yes') : t('no'),
        },
        {label: t('created_by'), value: tradeName.created ? <UserTag user={tradeName.created} /> : ''},
        {label: t('date'), value: tradeName.createdAt ? getDatetimeFormatter().format(tradeName.createdAt) : ''},
      ],

      properties,
      additionals,
      reconstitution,
      consumables,

      logistics: [
        {label: t('label_name'), value: tradeName.labelName},
        {label: t('national_id'), value: tradeName.nationalId},
        {label: t('atc_code'), value: tradeName.atcCode},
        {label: t('barcode_gtin'), value: tradeName.barcodeGtin},
        {
          label: t('external_info_link'),
          value: tradeName.externalInformationLink ? (
            <Link
              href={tradeName.externalInformationLink}
              target="_blank"
              rel="noopener noreferrer"
              variant="underline"
              colorPalette="cyan"
            >
              {tradeName.externalInformationLink} <LuExternalLink />
            </Link>
          ) : null,
        },
      ],
    }
  }, [t, tradeName])

  return (
    <Tabs.Content value="about" spaceY="8" display="flex" flexDirection="column">
      {tradeName.description && (
        <Alert.Root variant="subtle" status="info" display="flex" gapX="2">
          <Alert.Indicator />
          <Box>
            <Alert.Title fontSize="xs">{t('note')}</Alert.Title>
            <Alert.Content fontSize="2xs" wordBreak="break-word">
              {tradeName.description}
            </Alert.Content>
          </Box>
        </Alert.Root>
      )}

      <DataSection label={t('about')}>
        <DataList items={about} />
      </DataSection>

      <DataSection label={t('properties')}>
        <DataList items={properties} />
      </DataSection>

      <DataSection label={t('consumables')}>
        {consumables.length ? (
          <DataList items={consumables} />
        ) : (
          <Text color="fg.muted">{t('no_consumables_added_yet')}</Text>
        )}
      </DataSection>

      {Boolean(reconstitution.length) && (
        <DataSection
          label={
            <HStack justifyContent="space-between">
              {t('reconstitution')}

              <HStack gapX="2" width="max-content">
                <Status value={tradeName.reconstitutionRequired ? 'success' : 'error'} />
                <Span fontSize="sm" fontWeight="normal">
                  {tradeName.reconstitutionRequired ? t('required') : t('not_required')}
                </Span>
              </HStack>
            </HStack>
          }
        >
          <DataList items={reconstitution} />
        </DataSection>
      )}

      {Boolean(additionals.length) && (
        <DataSection label={t('additionals')}>
          <DataList items={additionals} />
        </DataSection>
      )}

      <DataSection label={t('logistics')}>
        <DataList items={logistics} />
      </DataSection>
    </Tabs.Content>
  )
}
